/**
 * WhatsApp Floating Action Button
 * Persistent FAB visible on all pages for quick WhatsApp contact
 */

import React, { useRef, useEffect } from 'react';
import {
  TouchableOpacity,
  StyleSheet,
  Animated,
  Linking,
  Platform,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { analytics, AnalyticsEvent } from '../../utils/analytics';
import { useChatVisibility } from '../../contexts/ChatVisibilityContext';
import { useOnboardingStore } from '../../store/onboardingStore';

const WHATSAPP_NUMBER = process.env.EXPO_PUBLIC_WHATSAPP_NUMBER || '+33760025005';
const WHATSAPP_GREEN = '#25D366';

export default function WhatsAppFloatingButton() {
  const { isChatVisible } = useChatVisibility();
  const { currentStep } = useOnboardingStore();
  const isOnboarding = currentStep != null && currentStep.step !== 'complete';
  const scaleAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    // Animate in on mount
    Animated.spring(scaleAnim, {
      toValue: 1,
      tension: 50,
      friction: 7,
      useNativeDriver: true,
    }).start();
  }, []);

  const handlePress = () => {
    const message = encodeURIComponent(
      "Bonjour, j'aimerais en savoir plus sur Wealfi."
    );
    const cleanNumber = WHATSAPP_NUMBER.replace(/[^0-9]/g, '');
    const url = `https://wa.me/${cleanNumber}?text=${message}`;

    analytics.trackEvent(AnalyticsEvent.WHATSAPP_FAB_CLICKED);
    Linking.openURL(url);
  };

  if (isChatVisible || isOnboarding) return null;

  return (
    <Animated.View
      style={[
        styles.container,
        { transform: [{ scale: scaleAnim }] },
      ]}
    >
      <TouchableOpacity
        style={styles.button}
        onPress={handlePress}
        activeOpacity={0.85}
        accessibilityLabel="Nous contacter sur WhatsApp"
        accessibilityRole="button"
      >
        <Icon name="whatsapp" size={28} color="#FFFFFF" />
      </TouchableOpacity>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 24,
    right: 24,
    zIndex: 1000,
    ...(Platform.OS === 'web'
      ? { position: 'fixed' as any }
      : {}),
  },
  button: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: WHATSAPP_GREEN,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
});
